import React, { useCallback, useState } from 'react';

const STORAGE_KEY = 'chronovela.skaldScribeBannerDismissedAt';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

function isBannerDismissed(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const dismissedAt = Number(raw);
    if (!Number.isFinite(dismissedAt)) return false;
    return Date.now() - dismissedAt < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}

function dismissBanner(): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    // Ignore storage failures — banner stays visible for this session only.
  }
}

export const SkaldScribeLaunchBanner: React.FC = () => {
  const [visible, setVisible] = useState(() => !isBannerDismissed());

  const handleDismiss = useCallback(() => {
    dismissBanner();
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label="Product announcement"
      className="relative w-full shrink-0 z-40 border-b border-cv-gold/25 bg-cv-page bg-gradient-to-r from-cv-gold/15 via-cv-gold/[0.06] to-cv-page shadow-[inset_0_1px_0_rgba(180,154,99,0.12)]"
    >
      <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-cv-gold/70 via-cv-gold-soft/50 to-cv-gold/20" />

      <div className="relative max-w-6xl mx-auto px-10 sm:px-12 py-3 min-h-[60px] flex items-center justify-center">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center">
          <p className="text-xs sm:text-sm text-cv-secondary leading-snug sm:whitespace-nowrap">
            Chronovela is now live as <strong className="font-bold text-cv-primary">Skald Scribe</strong> — generate
            full novels with true story continuity.
          </p>
          <a
            href="https://skaldscribe.com"
            className="inline-flex items-center justify-center shrink-0 rounded-lg bg-cv-gold hover:bg-cv-gold-soft text-cv-page font-bold text-xs sm:text-sm px-4 py-1.5 transition-all shadow-sm shadow-cv-gold/20 hover:shadow-cv-gold/35 hover:-translate-y-px"
          >
            Try Skald Scribe →
          </a>
        </div>

        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss announcement"
          className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-cv-muted opacity-40 hover:opacity-100 hover:text-cv-primary hover:bg-cv-panel/60 transition-all"
        >
          <span aria-hidden="true" className="text-lg leading-none">
            ×
          </span>
        </button>
      </div>
    </div>
  );
};
